import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, IconButton, Typography, useMediaQuery } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import { getItemBatches } from "../../api";
import { useCompany } from "../../Context/CompanyContext";
import { daysUntilExpiry, formatExpiry } from "./ExpiryChip";

const EPS = 0.0001;
// After the pop-up is dismissed (X, Cancel, outside click, Escape, or a choice) its backdrop stays for
// this long while it fades. A click that was already on its way - a touch screen's delayed "ghost" click,
// a bouncing button - lands on the backdrop instead of the item card / cart behind it, which used to add
// that item by accident. The parent is told once the fade is over.
const EXIT_MS = 340;

const TONES = {
  expired: { bar: "#dc2626", bg: "#fef2f2", text: "#b91c1c", label: "Expired" },
  near: { bar: "#f59e0b", bg: "#fffbeb", text: "#b45309", label: "Near expiry" },
  ok: { bar: "#16a34a", bg: "#f0fdf4", text: "#15803d", label: "Fresh" },
  none: { bar: "#94a3b8", bg: "#f8fafc", text: "#475569", label: "No expiry" },
};

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const keyOf = (b) => `${b.batchNo ?? ""}|${b.expiryDate ? new Date(b.expiryDate).getTime() : ""}`;
const expiryMs = (d) => (d ? new Date(d).getTime() : Infinity);
const hint = (days) =>
  days === null ? "no expiry date" : days < 0 ? `expired ${Math.abs(days)}d ago` : days === 0 ? "expires today" : `${days}d left`;

// Nearest-expiry-first fill, the same order the server uses when nothing is chosen.
const autoFill = (batches, needed, skipExpired) => {
  const out = {};
  let remaining = needed;
  for (const b of batches) {
    if (remaining <= EPS) break;
    if (skipExpired && b.expired) continue;
    const take = Math.min(b.qty, remaining);
    if (take > EPS) {
      out[keyOf(b)] = round2(take);
      remaining -= take;
    }
  }
  return out;
};

/**
 * Lets the cashier choose which batches a line is sold from.
 *  - item:         the cart line
 *  - needed:       quantity to cover, in the item's STOCK unit
 *  - initialPicks: [{ batchNo, expiryDate, qty }] chosen earlier (stock unit)
 *  - onConfirm(picks): picks = [] means "automatic, nearest expiry first"
 *  - startEmpty:   open with NOTHING selected (the store asked for the batch to be chosen by the
 *                  cashier, so the system must not pre-pick the nearest-expiry - possibly expired -
 *                  batch). Without it, an empty `initialPicks` starts from the automatic fill.
 *  - freeQty:      the cashier decides the QUANTITY too: take any amount from any batches (several at
 *                  once). `needed` is then only the starting suggestion, and `inCart` the quantity
 *                  already on the line (shown for context; the selection replaces the whole line).
 * The server re-checks every pick when the sale is saved.
 */
const BatchPickerDialog = ({ open, onClose, item, needed, stockUnit = "", initialPicks = [], invoiceNumber, onConfirm, freeQty = false, inCart = 0, startEmpty = false }) => {
  const { company } = useCompany();
  const narrow = useMediaQuery("(max-width:600px)");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [batches, setBatches] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [ackSkip, setAckSkip] = useState(false);
  // A fast double-click on Confirm / Use automatic must not fire twice (the parent would add twice).
  const doneRef = useRef(false);
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const leave = (callback) => {
    if (leavingRef.current) return; // dismissing twice (X then backdrop) does nothing extra
    leavingRef.current = true;
    setLeaving(true);
    timerRef.current = setTimeout(callback, EXIT_MS);
  };

  const expiredMode = company?.expiredSaleMode || "allow";
  const skipRule = company?.batchSkipRule || "warn";
  const nearDays = Number(company?.nearExpiryDays) || 30;

  useEffect(() => {
    if (!open || !item?._id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setAckSkip(false);
    doneRef.current = false;
    leavingRef.current = false;
    setLeaving(false);
    getItemBatches(item._id, invoiceNumber ? { invoiceNumber } : undefined)
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data?.batches || []).filter((b) => b.qty > EPS);
        setBatches(list);
        const start = {};
        (initialPicks || []).forEach((p) => {
          const match = list.find((b) => keyOf(b) === keyOf(p));
          if (match && Number(p.qty) > 0) start[keyOf(match)] = Math.min(round2(p.qty), round2(match.qty));
        });
        setAlloc(Object.keys(start).length ? start : startEmpty ? {} : autoFill(list, needed, expiredMode === "block"));
      })
      .catch((err) => !cancelled && setError(err?.response?.data?.message || "Could not load the batches"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?._id]);

  const level = (b) => {
    const d = b.expiryDate ? daysUntilExpiry(b.expiryDate) : null;
    return d === null ? "none" : d < 0 ? "expired" : d <= nearDays ? "near" : "ok";
  };

  const totalAvailable = useMemo(() => batches.reduce((s, b) => s + b.qty, 0), [batches]);
  const selected = useMemo(() => round2(Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0)), [alloc]);
  const covered = freeQty
    ? selected > 0.0049
    : Math.abs(selected - needed) < 0.005 || (totalAvailable < needed && Math.abs(selected - totalAvailable) < 0.005);
  const over = freeQty ? false : selected > needed + 0.005;
  const missing = Math.max(round2(needed - selected), 0);

  const pickedExpired = batches.filter((b) => (alloc[keyOf(b)] || 0) > EPS && b.expired);
  const expiredBlocked = expiredMode === "block" && pickedExpired.length > 0;

  // A picked batch that expires LATER than a batch which still has stock left over.
  const skipped = useMemo(() => {
    const result = [];
    batches.forEach((b) => {
      const taken = Number(alloc[keyOf(b)]) || 0;
      if (taken <= EPS) return;
      batches.forEach((o) => {
        if (o === b) return;
        const leftover = o.qty - (Number(alloc[keyOf(o)]) || 0);
        if (leftover > EPS && expiryMs(o.expiryDate) < expiryMs(b.expiryDate) && !(expiredMode === "block" && o.expired)) {
          if (!result.includes(o)) result.push(o);
        }
      });
    });
    return result;
  }, [alloc, batches, expiredMode]);
  const skipBlocked = skipRule === "block" && skipped.length > 0;
  const skipWarn = skipRule === "warn" && skipped.length > 0;

  const setQty = (b, value) => {
    setAckSkip(false);
    const n = value === "" ? "" : Math.max(0, Math.min(Number(value), b.qty));
    setAlloc((prev) => ({ ...prev, [keyOf(b)]: Number.isNaN(n) ? 0 : n }));
  };
  const step = (b, delta) => setQty(b, round2((Number(alloc[keyOf(b)]) || 0) + delta));
  // Fill this batch with whatever is still missing (or all it holds).
  const fillBatch = (b) => setQty(b, freeQty ? b.qty : Math.min(b.qty, round2((Number(alloc[keyOf(b)]) || 0) + missing)));

  const canConfirm = covered && !over && !expiredBlocked && !skipBlocked && batches.length > 0;

  // Low stock is judged on what can still be SOLD (expired units do not count): warn when this
  // selection would leave the item at or below its minimum level.
  const minQty = Number(item?.stockQty?.minQty) > 0 ? Number(item.stockQty.minQty) : null;
  const sellableAll = batches.filter((b) => !b.expired).reduce((s, b) => s + b.qty, 0);
  const sellableTaken = batches.filter((b) => !b.expired).reduce((s, b) => s + (Number(alloc[keyOf(b)]) || 0), 0);
  const sellableAfter = round2(Math.max(sellableAll - sellableTaken, 0));
  const lowAfter = minQty !== null && selected > 0 && sellableAfter <= minQty;

  const finish = (picks) => {
    if (doneRef.current) return;
    doneRef.current = true;
    leave(() => onConfirm(picks));
  };
  const dismiss = () => leave(onClose);

  const confirm = () => {
    if (skipWarn && !ackSkip) {
      setAckSkip(true);
      return;
    }
    const picks = batches
      .map((b) => ({ batchNo: b.batchNo, expiryDate: b.expiryDate, qty: round2(Number(alloc[keyOf(b)]) || 0) }))
      .filter((p) => p.qty > EPS);
    finish(picks);
  };

  const status = freeQty
    ? selected > 0
      ? { tone: "good", text: inCart > 0 ? `New total ${selected} ${stockUnit} (was ${round2(inCart)})` : "Ready to add" }
      : { tone: "warn", text: "Pick at least one" }
    : !covered
    ? over
      ? { tone: "bad", text: `${round2(selected - needed)} ${stockUnit} too many selected` }
      : { tone: "warn", text: `Pick ${missing} ${stockUnit} more` }
    : { tone: "good", text: "Quantity covered" };
  const statusColor = { good: "#15803d", warn: "#b45309", bad: "#b91c1c" }[status.tone];
  const pct = freeQty ? (selected > 0 ? 100 : 0) : needed > 0 ? Math.min(100, (selected / needed) * 100) : 0;

  return (
    <Dialog
      open={open && !leaving}
      onClose={dismiss}
      transitionDuration={{ enter: 150, exit: EXIT_MS - 40 }}
      fullWidth
      maxWidth="sm"
      fullScreen={narrow}
      PaperProps={{ style: { borderRadius: narrow ? 0 : 18, overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(15,23,42,0.35)" } }}
    >
      <div
        style={{
          background: "linear-gradient(135deg,#ea580c,#f97316)",
          color: "#fff",
          padding: "11px 14px",
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <EventAvailableOutlinedIcon style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography style={{ fontWeight: 800, fontSize: "1rem", lineHeight: 1.25 }}>Choose batch</Typography>
          <Typography style={{ opacity: 0.95, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {freeQty ? `${item?.itemName} · take any amount from any batches` : `${item?.itemName} · selling ${round2(needed)} ${stockUnit}`}
          </Typography>
        </div>
        <IconButton size="small" onClick={dismiss} style={{ color: "#fff" }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div style={{ padding: "8px 14px 0", background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", fontWeight: 700, marginBottom: 6 }}>
          <span style={{ color: "#334155" }}>
            {freeQty
              ? `Selected ${selected} ${stockUnit}${inCart > 0 ? ` · in cart now: ${round2(inCart)}` : ""}`
              : `Selected ${selected} of ${round2(needed)} ${stockUnit}`}
          </span>
          <span style={{ color: statusColor }}>{status.text}</span>
        </div>
        <div style={{ height: 6, borderRadius: 9999, background: "#e2e8f0", overflow: "hidden" }}>
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: over ? "#dc2626" : covered ? "#16a34a" : "#f59e0b",
              transition: "width .2s, background .2s",
            }}
          />
        </div>
      </div>

      <div style={{ padding: "8px 14px", background: "#fff", maxHeight: narrow ? "none" : "56vh", overflowY: "auto", flex: 1 }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 28 }}>
            <CircularProgress size={28} />
          </div>
        )}
        {!loading && error && <Typography style={{ color: "#b91c1c", fontWeight: 600 }}>{error}</Typography>}
        {!loading && !error && batches.length === 0 && (
          <Typography style={{ color: "#475569", padding: "10px 0" }}>There is no batch stock for this item.</Typography>
        )}

        {!loading &&
          batches.map((b) => {
            const lv = level(b);
            const tone = TONES[lv];
            const days = b.expiryDate ? daysUntilExpiry(b.expiryDate) : null;
            const taken = Number(alloc[keyOf(b)]) || 0;
            const active = taken > EPS;
            const blockedRow = expiredMode === "block" && b.expired;
            return (
              <div
                key={keyOf(b)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap", // on a narrow screen the quantity controls drop under the details
                  borderRadius: 10,
                  border: `1.5px solid ${active ? tone.bar : "#e2e8f0"}`,
                  borderLeft: `4px solid ${tone.bar}`,
                  background: active ? tone.bg : "#fff",
                  marginBottom: 6,
                  opacity: blockedRow ? 0.6 : 1,
                  transition: "border-color .15s, background .15s",
                }}
              >
                <div style={{ flex: "1 1 140px", minWidth: 0, padding: "6px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, color: "#0f172a", fontSize: "0.88rem" }}>
                      {b.batchNo ? `Batch ${b.batchNo}` : "No batch"}
                    </span>
                    <span
                      style={{ fontSize: "0.64rem", fontWeight: 800, padding: "0 7px", borderRadius: 9999, background: tone.bar, color: "#fff", lineHeight: 1.6, letterSpacing: 0.2 }}
                    >
                      {tone.label}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0 8px", fontSize: "0.74rem", color: "#475569", marginTop: 1, lineHeight: 1.4 }}>
                    <span style={{ color: tone.text, fontWeight: 700 }}>
                      {b.expiryDate ? `${formatExpiry(b.expiryDate)} · ${hint(days)}` : "No expiry date"}
                    </span>
                    <span>
                      In stock <b style={{ color: "#0f172a" }}>{round2(b.qty)}</b> {stockUnit}
                    </span>
                    {blockedRow && <span style={{ color: "#b91c1c", fontWeight: 700 }}>can't be sold</span>}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "2px 8px 2px 4px", marginLeft: "auto", flex: "none" }}>
                  <IconButton size="small" style={{ padding: 4 }} onClick={() => step(b, -1)} disabled={blockedRow || taken <= 0} aria-label="Less">
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <input
                    type="number"
                    min={0}
                    max={b.qty}
                    step="any"
                    inputMode="decimal"
                    value={alloc[keyOf(b)] ?? ""}
                    disabled={blockedRow}
                    onChange={(e) => setQty(b, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`Quantity from batch ${b.batchNo || "none"}`}
                    style={{
                      width: 50,
                      textAlign: "center",
                      fontWeight: 800,
                      fontSize: "0.95rem",
                      padding: "4px 2px",
                      borderRadius: 7,
                      border: "1px solid #cbd5e1",
                      outline: "none",
                      background: "#fff",
                    }}
                  />
                  <IconButton size="small" style={{ padding: 4 }} onClick={() => step(b, 1)} disabled={blockedRow || taken >= b.qty - EPS} aria-label="More">
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    disabled={blockedRow || (!freeQty && missing <= EPS) || taken >= b.qty - EPS}
                    onClick={() => fillBatch(b)}
                    title={freeQty ? "Take everything this batch holds" : "Fill the remaining quantity from this batch"}
                    style={{ textTransform: "none", fontWeight: 700, fontSize: "0.7rem", minWidth: 0, padding: "2px 7px", marginLeft: 2, color: "#ea580c", background: "rgba(234,88,12,0.08)", borderRadius: 8 }}
                  >
                    {freeQty ? "Take all" : "Fill"}
                  </Button>
                </div>
              </div>
            );
          })}
        {expiredBlocked && (
          <Notice tone="bad">Expired stock can't be sold under the current store rule. Remove the expired batch.</Notice>
        )}
        {!expiredBlocked && pickedExpired.length > 0 && expiredMode === "warn" && (
          <Notice tone="warn">You picked expired stock. Your store asks for a confirmation on these sales.</Notice>
        )}
        {skipBlocked && (
          <Notice tone="bad">
            Batch {skipped[0].batchNo || "(no batch)"} expires sooner and still has stock. Sell it first - your store doesn't allow skipping it.
          </Notice>
        )}
        {lowAfter && (
          <Notice tone="warn">
            Low stock: only {sellableAfter} {stockUnit} (not expired) would be left after this sale - the minimum is {minQty}.
          </Notice>
        )}
        {skipWarn && (
          <Notice tone="warn">
            Batch {skipped[0].batchNo || "(no batch)"} ({formatExpiry(skipped[0].expiryDate)}) expires sooner and still has stock left.
            {ackSkip ? " Press Confirm anyway to continue." : ""}
          </Notice>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "space-between",
          flexWrap: "wrap",
          padding: "9px 14px 12px",
          borderTop: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <Button
          onClick={() => finish([])}
          style={{ textTransform: "none", fontWeight: 700, color: "#475569", borderRadius: 10 }}
          title="Sell from the batch that expires first"
        >
          Use automatic
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={dismiss} style={{ textTransform: "none", fontWeight: 700, color: "#475569", borderRadius: 10 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!canConfirm}
            onClick={confirm}
            style={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 10,
              padding: "6px 22px",
              background: canConfirm ? (skipWarn && ackSkip ? "#dc2626" : "#ea580c") : undefined,
              color: canConfirm ? "#fff" : undefined,
            }}
          >
            {skipWarn && ackSkip ? "Confirm anyway" : freeQty ? (inCart > 0 ? "Update cart" : "Add to cart") : "Confirm batches"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const Notice = ({ tone, children }) => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      padding: "9px 12px",
      borderRadius: 10,
      marginTop: 2,
      marginBottom: 8,
      fontSize: "0.8rem",
      fontWeight: 600,
      lineHeight: 1.45,
      background: tone === "bad" ? "#fef2f2" : "#fffbeb",
      color: tone === "bad" ? "#b91c1c" : "#b45309",
      border: `1px solid ${tone === "bad" ? "#fecaca" : "#fde68a"}`,
    }}
  >
    <WarningRoundedIcon style={{ fontSize: 18, flex: "none", marginTop: 1 }} />
    <span>{children}</span>
  </div>
);

export default BatchPickerDialog;
